import * as React from 'react'
import { Factory as FactoryIcon, Users as UsersIcon } from 'lucide-react'
import type {
  PlantEquipment,
  PlantLineLaborStation,
  PlantLineOperation,
  PlantLineOperations,
  PlantOperationLabor
} from '@axilon-apps/domains-ontology-ui-state'
import { SectionHeader } from './entity/SectionHeader'
import { FlowTile, FlowTileCaret } from './FlowTile'
import {
  formatHeadcount,
  formatOperationKind,
  formatRoleChipLabel,
  formatUsageKind
} from './labor-format'
import {
  buildOperationEquipmentLinks,
  formatOperationResourceSummary,
  groupOperationEquipment,
  isFullOrderCoverage,
  orderOperations,
  otherEquipmentOnLine,
  type OperationEquipmentLinks
} from './operation-work'
import { PlantHref } from './PlantHref'
import { UnorderedNotice } from './UnorderedNotice'

export function OperationWorkSections({
  siteId,
  labor,
  directEquipment
}: {
  siteId: string
  labor: PlantLineOperations
  directEquipment: PlantEquipment[]
}): React.ReactElement | null {
  if (labor.mode === 'operations') {
    if (labor.operations.length === 0) return null
    return (
      <LineOperationSections
        siteId={siteId}
        operations={labor.operations}
        orderCoverage={labor.orderCoverage}
        directEquipment={directEquipment}
      />
    )
  }

  if (labor.legacy.stations.length === 0) return null
  return (
    <LegacyOperationSections
      siteId={siteId}
      stations={labor.legacy.stations}
      flowNeighbors={labor.legacy.flowNeighbors}
      flowEdges={labor.legacy.flowEdges}
      directEquipment={directEquipment}
    />
  )
}

function LineOperationSections({
  siteId,
  operations,
  orderCoverage,
  directEquipment
}: {
  siteId: string
  operations: PlantLineOperation[]
  orderCoverage: PlantLineOperations['orderCoverage']
  directEquipment: PlantEquipment[]
}): React.ReactElement {
  const complete = isFullOrderCoverage(orderCoverage)
  const otherEquipment = otherEquipmentOnLine(operations, directEquipment)

  return (
    <>
      <section>
        <SectionHeader
          label="Operation path"
          subtitle={`${operations.length} ordered ${
            operations.length === 1 ? 'operation' : 'operations'
          }`}
        />
        <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
          {!complete && (
            <UnorderedNotice>
              These operations do not have a complete sequence_number — listed with available order,
              not guaranteed execution order.
            </UnorderedNotice>
          )}
          <div className="overflow-x-auto pb-1">
            <div className="flex min-w-max items-start gap-1.5">
              {operations.map((operation, index) => (
                <React.Fragment key={operation.operation.id}>
                  <div className="flex w-55 flex-col gap-1.5">
                    <OperationPathTile operation={operation} position={index + 1} />
                    <ExceptionRows operation={operation} />
                  </div>
                  {complete && index < operations.length - 1 && <FlowTileCaret />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ResourcesByOperationSection
        siteId={siteId}
        operations={operations}
        otherEquipment={otherEquipment}
      />
    </>
  )
}

function OperationPathTile({
  operation,
  position
}: {
  operation: PlantLineOperation
  position: number
}): React.ReactElement {
  const kind = formatOperationKind(operation.operation.operation_kind)
  const resources = formatOperationResourceSummary(operation)
  const subtitle = [`${position}`, kind, resources].filter(Boolean).join(' · ')
  return (
    <div className="[&>a]:min-h-16 [&>a]:w-full">
      <FlowTile
        kind="operation"
        id={operation.operation.id}
        label="LineOperation"
        node={operation.operation}
        name={operation.operation.name}
        subtitle={subtitle}
      />
    </div>
  )
}

function ExceptionRows({
  operation
}: {
  operation: PlantLineOperation
}): React.ReactElement | null {
  if (operation.recoveredBy.length === 0 && operation.fallbacks.length === 0) return null
  return (
    <div className="flex flex-col gap-1 px-0.5">
      {operation.recoveredBy.map((op) => (
        <PlantHref
          key={op.id}
          id={op.id}
          label="LineOperation"
          node={op}
          className="text-11 text-ink-3 no-underline hover:text-brand hover:underline"
        >
          If this fails — recovered by: {op.name}
        </PlantHref>
      ))}
      {operation.fallbacks.map((op) => (
        <PlantHref
          key={op.id}
          id={op.id}
          label="LineOperation"
          node={op}
          className="text-11 text-ink-3 no-underline hover:text-brand hover:underline"
        >
          If this fails — fallback: {op.name}
        </PlantHref>
      ))}
    </div>
  )
}

function ResourcesByOperationSection({
  siteId,
  operations,
  otherEquipment
}: {
  siteId: string
  operations: PlantLineOperation[]
  otherEquipment: PlantEquipment[]
}): React.ReactElement {
  return (
    <section>
      <SectionHeader
        label="Resources by operation"
        subtitle="People and equipment, grouped by step"
      />
      <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
        <div className="flex flex-col divide-y divide-border">
          {operations.map((operation, index) => (
            <OperationResourceRow
              key={operation.operation.id}
              siteId={siteId}
              operation={operation}
              position={index + 1}
            />
          ))}
        </div>

        {otherEquipment.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <div className="mb-2 text-10 font-semibold uppercase tracking-[0.08em] text-ink-4">
              Other equipment on this line
            </div>
            <div className="flex flex-wrap gap-2">
              {otherEquipment.map((equipment) => (
                <EquipmentChip
                  key={equipment.id}
                  siteId={siteId}
                  equipment={equipment}
                  accent={false}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function OperationResourceRow({
  siteId,
  operation,
  position
}: {
  siteId: string
  operation: PlantLineOperation
  position: number
}): React.ReactElement {
  const grouped = groupOperationEquipment(operation.equipment)

  return (
    <div className="grid items-start gap-3 py-4 first:pt-0 last:pb-0 lg:grid-cols-[minmax(12.5rem,0.75fr)_minmax(0,2fr)] lg:gap-5">
      <div className="flex min-w-0 items-start gap-2.5 pt-0.5">
        <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-bg-sunk text-12 font-semibold leading-none text-ink-2">
          {position}
        </span>
        <div className="min-w-0">
          <PlantHref
            id={operation.operation.id}
            label="LineOperation"
            node={operation.operation}
            className="block min-w-0 text-13 font-semibold leading-snug text-ink no-underline hover:text-brand hover:underline"
          >
            {operation.operation.name}
          </PlantHref>
          {operation.procedures.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {operation.procedures.map((proc) => (
                <PlantHref
                  key={proc.id}
                  id={proc.id}
                  label="Procedure"
                  node={proc}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-11 font-medium text-amber-800 no-underline hover:bg-amber-500/20 dark:text-amber-300"
                >
                  <ResourceChipLabel>Procedure</ResourceChipLabel>
                  {proc.name}
                </PlantHref>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="grid gap-2">
        <ResourceBand
          icon={<UsersIcon size={13} aria-hidden />}
          title="People"
          className="border-sky-200/70 bg-sky-50/50 dark:border-sky-900/70 dark:bg-sky-950/20"
        >
          <LaborParticipationList labor={operation.labor} />
        </ResourceBand>
        <ResourceBand
          icon={<FactoryIcon size={13} aria-hidden />}
          title="Material & equipment"
          className="bg-bg-sunk/40"
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <EquipmentRelationship siteId={siteId} label="Receives from" equipment={grouped.from} />
            <EquipmentUsesRelationship siteId={siteId} uses={grouped.uses} />
            <EquipmentRelationship siteId={siteId} label="Sends to" equipment={grouped.to} />
          </div>
        </ResourceBand>
      </div>
    </div>
  )
}

function LaborParticipationList({ labor }: { labor: PlantOperationLabor[] }): React.ReactElement {
  if (labor.length === 0) {
    return <span className="text-11 text-ink-4">No human labor required</span>
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {labor.map((row) => (
        <div key={row.task.id} className="flex flex-wrap items-center gap-1.5">
          {row.staffing.length === 0 ? (
            <span className="inline-flex rounded-full border border-dashed border-border px-2 py-0.5 text-11 text-ink-4">
              Role not specified
            </span>
          ) : (
            row.staffing.map((staff) => (
              <PlantHref
                key={staff.role.id}
                id={staff.role.id}
                label="Role"
                node={staff.role}
                className="inline-flex items-center gap-1 rounded-full border border-violet-400/30 bg-violet-500/10 px-2 py-0.5 text-11 font-medium text-violet-700 no-underline hover:bg-violet-500/20 dark:text-violet-300"
              >
                <ResourceChipLabel>Role</ResourceChipLabel>
                {formatRoleChipLabel(staff.role.name, staff.minGrade, staff.maxGrade)}
              </PlantHref>
            ))
          )}
          <PlantHref
            id={row.task.id}
            label="HumanLabor"
            node={row.task}
            className="inline-flex items-center gap-1 rounded-full border border-sky-400/40 bg-sky-500/10 px-2 py-0.5 text-11 font-medium text-sky-700 no-underline hover:bg-sky-500/20 dark:text-sky-300"
          >
            <ResourceChipLabel>Labor</ResourceChipLabel>
            {row.task.name}
            {formatHeadcount(row.numEmployees) ? ` · ${formatHeadcount(row.numEmployees)}` : ''}
          </PlantHref>
          {row.procedures.map((proc) => (
            <PlantHref
              key={proc.id}
              id={proc.id}
              label="Procedure"
              node={proc}
              className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-11 font-medium text-amber-800 no-underline hover:bg-amber-500/20 dark:text-amber-300"
            >
              <ResourceChipLabel>Procedure</ResourceChipLabel>
              {proc.name}
            </PlantHref>
          ))}
        </div>
      ))}
    </div>
  )
}

function ResourceChipLabel({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span className="font-semibold uppercase tracking-[0.05em] opacity-70">{children} ·</span>
}

function ResourceBand({
  icon,
  title,
  children,
  className = ''
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  className?: string
}): React.ReactElement {
  return (
    <div className={`rounded-lg border border-border px-3 py-2.5 ${className}`}>
      <div className="mb-2 flex items-center gap-1.5 text-11 font-semibold text-ink-2">
        <span className="text-ink-3">{icon}</span>
        {title}
      </div>
      {children}
    </div>
  )
}

function LegacyOperationSections({
  siteId,
  stations,
  flowNeighbors,
  flowEdges,
  directEquipment
}: {
  siteId: string
  stations: PlantLineLaborStation[]
  flowNeighbors: PlantEquipment[]
  flowEdges: PlantLineOperations['legacy']['flowEdges']
  directEquipment: PlantEquipment[]
}): React.ReactElement {
  const model = React.useMemo(() => {
    const order = orderOperations(stations)
    const equipmentLinks = buildOperationEquipmentLinks(
      order.ordered,
      directEquipment,
      flowNeighbors,
      flowEdges
    )
    const linkedEquipmentIds = new Set(
      equipmentLinks.flatMap((row) => [...row.from, ...row.uses, ...row.to].map((eq) => eq.id))
    )
    return {
      order,
      equipmentLinks,
      otherEquipment: directEquipment.filter((eq) => !linkedEquipmentIds.has(eq.id))
    }
  }, [stations, flowNeighbors, flowEdges, directEquipment])

  return (
    <>
      <LegacyOperationPathSection order={model.order} />
      <LegacyEquipmentSection
        siteId={siteId}
        links={model.equipmentLinks}
        otherEquipment={model.otherEquipment}
      />
    </>
  )
}

function LegacyOperationPathSection({
  order
}: {
  order: ReturnType<typeof orderOperations>
}): React.ReactElement {
  return (
    <section>
      <SectionHeader
        label="Operation path"
        subtitle={`${order.ordered.length} ordered ${
          order.ordered.length === 1 ? 'operation' : 'operations'
        }`}
      />
      <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
        {!order.complete && (
          <UnorderedNotice>
            These operations do not have a complete sequence_number — listed alphabetically, not in
            execution order.
          </UnorderedNotice>
        )}
        <div className="overflow-x-auto pb-1">
          <div className="flex min-w-max items-center gap-1.5">
            {order.ordered.map((station, index) => (
              <React.Fragment key={station.task.id}>
                <LegacyOperationTile station={station} position={index + 1} />
                {order.complete && index < order.ordered.length - 1 && <FlowTileCaret />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function LegacyOperationTile({
  station,
  position
}: {
  station: PlantLineLaborStation
  position: number
}): React.ReactElement {
  const kind = formatOperationKind(station.task.operation_kind)
  const headcount = formatHeadcount(station.numEmployees)
  const subtitle = [`${position}`, kind, headcount].filter(Boolean).join(' · ')
  return (
    <div className="[&>a]:min-h-16 [&>a]:w-55">
      <FlowTile
        kind="labor"
        id={station.task.id}
        label="HumanLabor"
        node={station.task}
        name={station.task.name}
        subtitle={subtitle}
      />
    </div>
  )
}

function LegacyEquipmentSection({
  siteId,
  links,
  otherEquipment
}: {
  siteId: string
  links: OperationEquipmentLinks[]
  otherEquipment: PlantEquipment[]
}): React.ReactElement {
  const linkedCount = new Set(
    links.flatMap((row) => [...row.from, ...row.uses, ...row.to].map((eq) => eq.id))
  ).size

  return (
    <section>
      <SectionHeader
        label="Equipment by operation"
        subtitle={`${linkedCount} linked ${linkedCount === 1 ? 'item' : 'items'}`}
      />
      <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
        <div className="flex flex-col divide-y divide-border">
          {links.map((row, index) => (
            <div
              key={row.station.task.id}
              className="grid gap-3 py-3 first:pt-0 last:pb-0 lg:grid-cols-[minmax(11.25rem,0.8fr)_minmax(0,2fr)]"
            >
              <div className="flex min-w-0 items-start gap-2">
                <span className="grid size-6 shrink-0 place-items-center rounded-full bg-bg-sunk text-11 font-semibold text-ink-2">
                  {index + 1}
                </span>
                <PlantHref
                  id={row.station.task.id}
                  label="HumanLabor"
                  node={row.station.task}
                  className="min-w-0 text-13 font-semibold text-ink no-underline hover:text-brand hover:underline"
                >
                  {row.station.task.name}
                </PlantHref>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <EquipmentRelationship siteId={siteId} label="Receives from" equipment={row.from} />
                <EquipmentRelationship siteId={siteId} label="Uses" equipment={row.uses} accent />
                <EquipmentRelationship siteId={siteId} label="Feeds to" equipment={row.to} />
              </div>
            </div>
          ))}
        </div>

        {otherEquipment.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <div className="mb-2 text-10 font-semibold uppercase tracking-[0.08em] text-ink-4">
              Other equipment on this line
            </div>
            <div className="flex flex-wrap gap-2">
              {otherEquipment.map((equipment) => (
                <EquipmentChip
                  key={equipment.id}
                  siteId={siteId}
                  equipment={equipment}
                  accent={false}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function EquipmentRelationship({
  siteId,
  label,
  equipment,
  accent = false
}: {
  siteId: string
  label: string
  equipment: PlantEquipment[]
  accent?: boolean
}): React.ReactElement {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-11 font-medium text-ink-3">{label}</div>
      {equipment.length === 0 ? (
        <span className="text-11 text-ink-4">—</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {equipment.map((item) => (
            <EquipmentChip key={item.id} siteId={siteId} equipment={item} accent={accent} />
          ))}
        </div>
      )}
    </div>
  )
}

function EquipmentUsesRelationship({
  siteId,
  uses
}: {
  siteId: string
  uses: Array<{ equipment: PlantEquipment; usageKind?: string }>
}): React.ReactElement {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-11 font-medium text-ink-3">Works with</div>
      {uses.length === 0 ? (
        <span className="text-11 text-ink-4">—</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {uses.map((row) => (
            <EquipmentChip
              key={row.equipment.id}
              siteId={siteId}
              equipment={row.equipment}
              accent
              title={formatUsageKind(row.usageKind) ?? undefined}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function EquipmentChip({
  siteId,
  equipment,
  accent,
  title
}: {
  siteId: string
  equipment: PlantEquipment
  accent: boolean
  title?: string
}): React.ReactElement {
  return (
    <PlantHref
      id={equipment.id}
      label="Equipment"
      node={{ ...equipment, siteId }}
      title={title}
      className={
        accent
          ? 'inline-flex rounded-full border border-sky-400/40 bg-sky-500/10 px-2 py-0.5 text-11 font-medium text-sky-700 no-underline hover:bg-sky-500/20 dark:text-sky-300'
          : 'inline-flex rounded-full border border-border bg-bg-sunk px-2 py-0.5 text-11 font-medium text-ink-2 no-underline hover:bg-accent'
      }
    >
      {equipment.name}
    </PlantHref>
  )
}
